import { useEffect, useRef, useState } from 'react';
import { btn, c, z } from '../../theme';

// The old dashboard's filter control: "Pod: All ▾" opening a checkbox list
// with Select all and Clear. Pods, channels — anything chosen several at a
// time. Kept as a dropdown rather than a row of pills because on the
// Director's floor there are nine pods and six channels, and the pills wrapped
// the header onto three lines.
//
// The button's text is the old app's rule, which distinguishes two states the
// pills could not: nothing chosen means EVERY one on a channel filter
// (`emptyMeansAll`) and NONE on a pod filter, and it says which.
//
// `align` is which edge of the button the menu hangs from, and it has to be
// told rather than worked out. The menu is 224px wide and the component cannot
// see where on the page it sits, so the caller is the only thing that knows.
// Getting it wrong is not cosmetic: on the Director and Supervisor views these
// controls open a LEFT-aligned row in the page body, and hanging the menu off
// its right edge pushed 224px of it leftward, past the content's left edge and
// under the sidebar — which is what the CS team reported as the menu being cut
// off. Default `right` because the VP page's copies live in PageHeader's
// right-aligned actions slot, where hanging left would run off screen instead.
export default function MultiSelect({
  label, options, selected, onChange,
  allLabel = 'All', noun = 'selected', emptyMeansAll = false, displayFn, align = 'right',
}) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const show = displayFn || ((v) => v);
  const summary = selected.length === 0 ? (emptyMeansAll ? allLabel : `No ${noun}`)
    : options.length && selected.length === options.length ? allLabel
      : selected.length === 1 ? show(selected[0]) : `${selected.length} ${noun}`;

  return (
    <div ref={box} style={s.wrap}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={s.button}
        aria-expanded={open} aria-haspopup="true">
        <span style={s.label}>{label}:</span>
        <span style={s.value}>{summary}</span>
        <span style={s.caret}>▾</span>
      </button>
      {open && (
        <div style={{ ...s.menu, ...(align === 'left' ? s.menuLeft : s.menuRight) }}
          role="group" aria-label={label}>
          <div style={s.menuHead}>
            <button type="button" onClick={() => onChange([...options])} style={s.linkRed}>Select all</button>
            <button type="button" onClick={() => onChange([])} style={s.link}>Clear</button>
          </div>
          <div style={s.list}>
            {options.map((o) => (
              <label key={o} style={s.row}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} style={s.check} />
                {show(o)}
              </label>
            ))}
            {!options.length && <div style={s.empty}>Nothing to choose.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:     { position: 'relative' },
  button:   { ...btn.secondaryXs, display: 'inline-flex', alignItems: 'center', gap: '6px' },
  label:    { color: c.textMuted },
  value:    { color: c.textStrong, fontWeight: 600 },
  caret:    { fontSize: '9px', opacity: 0.6 },
  // zIndex from the theme's stacking scale, not a local number: it has to beat
  // the sidebar's, and the two were set in different files. See `z` in theme.js.
  menu:     { position: 'absolute', marginTop: '4px', width: '224px', zIndex: z.menu,
    background: c.surface, border: `1px solid ${c.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(15,23,42,0.12)' },
  menuLeft:  { left: 0 },
  menuRight: { right: 0 },
  menuHead: { display: 'flex', justifyContent: 'space-between', padding: '6px 12px',
    borderBottom: `1px solid ${c.border}`, fontSize: '12px' },
  link:     { all: 'unset', cursor: 'pointer', color: c.textMuted, fontSize: '12px' },
  linkRed:  { all: 'unset', cursor: 'pointer', color: c.brand, fontSize: '12px' },
  list:     { maxHeight: '288px', overflowY: 'auto', padding: '4px 0' },
  row:      { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px',
    fontSize: '12px', color: c.textStrong, cursor: 'pointer' },
  check:    { accentColor: c.brand, cursor: 'pointer' },
  empty:    { padding: '8px 12px', fontSize: '12px', color: c.textMuted },
};
